/**
 * StickyATCBar — fixed bar pinned to the bottom of the viewport on mobile/tablet.
 *
 * Shows a quantity stepper + "Add to Cart" button (resolved variant SKU via
 * the lifted VariantSelector provider — SFCC-11390), with the eligibility /
 * free-shipping badge row beneath when applicable (prod parity).
 *
 * Reference: sfcc-website/org_hec/cartridge/templates/default/product/components/addToCartProductSticky.isml
 */
import { type RefObject, useEffect, useState } from "react";

import { VariantSelector } from "~/components/products/product-variant-selector";
import { TriButton } from "~/components/ui/tri-button";
import { QuantityStepper } from "~/hec/components/ui/quantity-stepper";
import { resolveQuantityCap } from "~/lib/quantity-cap";

import { useBasketActions } from "../../resources/hooks/basket-hooks";
import type { CrossSellItem } from "../../resources/schemas/basket";
import { EligibilityBadge, FreeShippingBadge } from "./product-badges";

interface StickyATCBarProps {
  productId: string;
  isOrderable?: boolean;
  /**
   * Master product's available-to-sell (`inventory.ats`) — caps the qty stepper
   * at live stock. This is the master/aggregate value; the bar's own
   * VariantSelector doesn't thread per-variant inventory here, so a variant with
   * lower stock than the master isn't reflected (SCAPI still rejects an
   * over-stock add regardless). Per-variant accuracy would mirror
   * ProductBuyForm's SelectedVariant inventory resolution — deferred.
   */
  stock?: number;
  crossSellSelections?: CrossSellItem[];
  isEligible?: boolean;
  siteLabel?: string;
  showsFreeShipping?: boolean;
}

export function StickyATCBar({
  productId,
  isOrderable = true,
  stock,
  crossSellSelections,
  isEligible,
  siteLabel,
  showsFreeShipping,
}: StickyATCBarProps) {
  const [quantity, setQuantity] = useState(1);
  const basket = useBasketActions();
  const isAdding = basket.fetcher.state !== "idle";
  // SFCC-11602 — drive the TriButton's "Done" morph explicitly so it doesn't
  // stick on the success state after an SCAPI inventory-validation error.
  // Mirrors the same wiring on the main ProductBuyForm.
  const lastSucceeded: boolean | undefined = !basket.fetcher.data
    ? undefined
    : "successfullyAdded" in basket.fetcher.data &&
      basket.fetcher.data.successfullyAdded === true;

  const showBadges = (isEligible && siteLabel) || showsFreeShipping;

  // DO NOT rename `sticky-atc-bar` or drop `fixed`: app.css's Zendesk launcher
  // rule (`body:has(.sticky-atc-bar.fixed) #launcher`) keys on both classes to
  // lift the chat button above this bar.
  return (
    <div className="sticky-atc-bar fixed bottom-0 left-0 z-40 w-full border-t border-gray-200 bg-white px-4 py-3 shadow-lg">
      {/* qty + ATC span the full viewport width. The compact stepper holds its
          minimum width and the ATC button takes the rest (flex-1) — which lands
          at ≈ prod/devhec's 31:69 split on a phone — and both share the thin
          50px height. No `container` cap — devhec is edge-to-edge with a ~15px
          inset (here `px-4`). */}
      <div className="flex items-center gap-3">
        <QuantityStepper
          quantity={quantity}
          onChange={setQuantity}
          max={resolveQuantityCap(stock)}
          mode="compact"
          thin
          testIdPrefix="pdp-sticky-quantity"
        />
        {/* Resolve the selected variant from the shared VariantSelector
            provider (lifted in product-details). The route-level `productId`
            is the master SKU — SCAPI rejects it on the basket — so submit
            the resolved `variant.productId`, mirroring ProductBuyForm
            (SFCC-11390). Falls back to `productId` for non-variant products. */}
        <VariantSelector.SelectedVariant>
          {({ variant }) => {
            const mainProductId = variant?.productId ?? productId;
            return (
              <TriButton
                type="button"
                variant="default"
                className="h-[50px] flex-1 !bg-primary font-grotesque font-medium hover:!bg-primary/90"
                disabled={!isOrderable || isAdding}
                isLoading={isAdding}
                data-testid="pdp-sticky-add-to-bag-button"
                succeeded={lastSucceeded}
                onClick={() => {
                  if (crossSellSelections && crossSellSelections.length > 0) {
                    basket.addMany([
                      { productId: mainProductId, quantity },
                      ...crossSellSelections,
                    ]);
                  } else {
                    basket.add({ productId: mainProductId }, quantity);
                  }
                }}
              >
                {isOrderable ? "Add to Cart" : "Sold Out"}
              </TriButton>
            );
          }}
        </VariantSelector.SelectedVariant>
      </div>

      {/* Eligibility + free-shipping badges under the qty/ATC row (restored
          from 96f27e88's trim — prod's sticky bar carries them on mobile).
          `sticky-atc-badges` is a hook for app.css: the Zendesk launcher
          lifts higher when this taller variant of the bar is mounted. */}
      {showBadges && (
        <div className="sticky-atc-badges mt-2 flex justify-around sm:gap-4">
          {/* FSA/HSA only — pairs with the free-shipping badge; WDH's
              eligibility shows via the SKU-area summary pill (SFCC-11640). */}
          {isEligible && siteLabel && showsFreeShipping && (
            <EligibilityBadge siteLabel={siteLabel} />
          )}
          {showsFreeShipping && <FreeShippingBadge />}
        </div>
      )}
    </div>
  );
}

/**
 * StickyATCSentinel — watches the inline qty+ATC element (passed via
 * `targetRef`) and toggles the mobile sticky bar. Renders nothing.
 *
 * The bar should be visible whenever the inline ATC is OFF screen in EITHER
 * direction — before the shopper has scrolled down to it (below the fold) and
 * after they've scrolled past it (above the top) — matching prod/devhec, and
 * hidden while any part of it is visible (so the inline and sticky buttons are
 * never on screen at once). Observing the inline element directly is correct in
 * all those states: any part intersecting -> hide; fully off-screen -> show.
 *
 * The element is passed explicitly rather than inferred from DOM position, so
 * reordering the surrounding markup can't silently change which element is
 * watched.
 */
export function StickyATCSentinel({
  targetRef,
  onVisibilityChange,
}: {
  targetRef: RefObject<HTMLElement | null>;
  onVisibilityChange: (visible: boolean) => void;
}) {
  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => onVisibilityChange(!entry.isIntersecting),
      { threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [targetRef, onVisibilityChange]);

  return null;
}
